import React, { useMemo, useState } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLoaderData, useNavigate } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import store from '../store';
import {
    selectIsAshford,
    selectPersonaId,
    selectStorageSize,
    selectStorageType,
    setStorageSize,
    setStorageType,
    setUnitData,
} from '../store/bookingSlice';

import Navbar from '../components/Navbar';
import Spinner from '../components/Spinner';
import Carousel from '../components/UnitListing/Carousel';
import DescriptorElement from '../components/UnitListing/Descriptor';
import QuoteForm from '../components/UnitListing/QuoteForm';

import {
    useGetUnitListQuery,
    usePostReservationMutation,
} from '../features/rapidstor/rapidstorApiSlice';

import classes from './UnitListing.module.scss';

import { getEarliestMoveInDate, logEvent, pickUnitType } from '../features/helpers';

function UnitListing() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();

    const storageSize = useSelector(selectStorageSize);
    const storageType = useSelector(selectStorageType);
    const personaId = useSelector(selectPersonaId);
    const isAshford = useSelector(selectIsAshford);

    const [currentPersonaId, setCurrentPersonaId] = useState<string | null>(personaId);
    const [quoteModalOpen, setQuoteModalOpen] = useState<boolean>(false);

    const [makeReservation, { isLoading: reservationIsLoading }] =
        usePostReservationMutation();

    const [activeDescriptor, setActiveDescriptor] = useState<number>(0);

    const {
        data: unitList,
        isLoading,
        isFetching,
    } = useGetUnitListQuery(
        { locationCode: location.locationCode, personaId: currentPersonaId },
        {
            refetchOnMountOrArgChange: true,
        }
    );

    useEffect(() => {
        if (!unitList) {
            return;
        }
        if (storageSize === null && storageType === null) {
            store.dispatch(setStorageSize(-1));
            store.dispatch(setStorageType('personal'));
        }
    }, [storageSize, storageType, unitList]);

    const descriptorList = useMemo(() => {
        if (!unitList) {
            return [];
        }
        const filtersIncludeDescriptor = (descriptor: Descriptor) => {
            if (descriptor.hidden) {
                return false;
            }
            if (descriptor.unitTypes.length < 1) {
                return false;
            }
            if (storageSize === -1) {
                return true;
            }
            if (storageSize === 0 && storageType === 'vehicle') {
                return true;
            }
            return true;
        };
        const ashfordFilter = (descriptor: Descriptor) => {
            if (isAshford) {
                return descriptor.unitTypes.some((unitType: UnitType) => {
                    return (
                        unitType.typeName.includes('Ash') ||
                        unitType.typeName.includes('Ashford')
                    );
                });
            } else if (location.slug === 'keswick') {
                return descriptor.unitTypes.some((unitType: UnitType) => {
                    return (
                        !unitType.typeName.includes('Ash') &&
                        !unitType.typeName.includes('Ashford')
                    );
                });
            } else {
                return true;
            }
        };
        const result = unitList.descriptors
            .filter(filtersIncludeDescriptor)
            .filter(ashfordFilter)
            .sort((a, b) => {
                return a.area - b.area;
            });
        return result;
    }, [unitList, storageSize, storageType, location.slug, isAshford]);

    useEffect(() => {
        if (descriptorList.length > 0) {
            // Find the first descriptor with appropriate size.
            const index = descriptorList.findIndex((descriptor) => {
                if (
                    storageSize === 0 &&
                    descriptor.area < location.rapidstor.smallMediumSizes
                ) {
                    return true;
                }
                if (
                    storageSize === location.rapidstor.smallMediumSizes &&
                    descriptor.area >= location.rapidstor.smallMediumSizes &&
                    descriptor.area < location.rapidstor.mediumLargeSizes
                ) {
                    return true;
                }
                if (
                    storageSize === location.rapidstor.mediumLargeSizes &&
                    descriptor.area >= location.rapidstor.mediumLargeSizes
                ) {
                    return true;
                }
                if (
                    storageSize === 999 &&
                    descriptor.area > location.rapidstor.mediumLargeSizes
                ) {
                    return true;
                }
            });
            setActiveDescriptor(index);
        }
    }, [
        descriptorList,
        storageSize,
        location.rapidstor.smallMediumSizes,
        location.rapidstor.mediumLargeSizes,
    ]);

    const onEmailQuoteClicked = (unitType: UnitType, descriptor: Descriptor) => {
        store.dispatch(
            setUnitData({
                location,
                unitType,
                descriptor,
                reservation: false,
            })
        );
        setQuoteModalOpen(true);
    };

    async function goToBookingForm(
        unitType: UnitType,
        descriptor: Descriptor,
        reservation: boolean
    ) {
        store.dispatch(setUnitData({ location, unitType, descriptor, reservation }));
        if (!unitType.units.length) {
            return logEvent(3, 'No units available');
        }
        try {
            const { token } = await makeReservation({
                locationCode: location.locationCode,
                payload: {
                    customListing: true,
                    neededDate: getEarliestMoveInDate(location),
                    timezoneOffset: location.timezoneOffset,
                    sourceUrl: window.location.href,
                    tenant: {
                        tenantId: null,
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                    },
                    unitType: unitType._id,
                    unitToUse: unitType.units[0]._id,
                    deal: unitType.deal ? unitType.deal._id : null,
                    persona: currentPersonaId ?? undefined,
                },
            }).unwrap();
            navigate(`/locations/${location.slug}/booking?token=${token}`);
        } catch (err) {
            logEvent(3, err as string);
        }
    }

    return (
        <>
            <Navbar
                label='Select your unit'
                setCurrentPersonaId={setCurrentPersonaId}
                storageTypeSelect
            />
            <div className={`${classes.container}`}>
                {isLoading || isFetching ? (
                    <Spinner center size={100} />
                ) : (
                    <>
                        {descriptorList.length === 0 ? (
                            <div>
                                <span>No units available</span>
                            </div>
                        ) : (
                            <>
                                <div className={`${classes.desktopCarousel}`}>
                                    <Carousel
                                        activeSlideIndex={activeDescriptor}
                                        onRequestChange={setActiveDescriptor}
                                        containerProps={{
                                            style: {
                                                margin: '0 auto',
                                                justifyContent: 'space-between',
                                                userSelect: 'none',
                                            },
                                        }}
                                        infinite={false}
                                        disableSwipeByTouch={false}
                                        swipeTreshold={60}
                                        forwardBtnProps={{
                                            style: {
                                                alignSelf: 'center',
                                            },
                                        }}
                                        activeSlideProps={{
                                            style: {
                                                opacity: 1,
                                            },
                                        }}
                                        backwardBtnProps={{
                                            style: {
                                                alignSelf: 'center',
                                            },
                                        }}
                                        itemsToShow={3}
                                        speed={300}
                                    >
                                        {descriptorList.map(
                                            (descriptor: Descriptor, index: number) => {
                                                if (
                                                    !descriptor.hidden &&
                                                    descriptor.unitTypes.length > 0
                                                ) {
                                                    return (
                                                        <DescriptorElement
                                                            key={index}
                                                            descriptor={descriptor}
                                                            recommended={false}
                                                            reservationIsLoading={
                                                                reservationIsLoading
                                                            }
                                                            onBookUnitClicked={() => {
                                                                const unitType =
                                                                    pickUnitType(
                                                                        descriptor
                                                                    );
                                                                goToBookingForm(
                                                                    unitType,
                                                                    descriptor,
                                                                    false
                                                                );
                                                            }}
                                                            locationSlug={location.slug}
                                                            onEmailQuoteClicked={() => {
                                                                const unitType =
                                                                    pickUnitType(
                                                                        descriptor
                                                                    );
                                                                onEmailQuoteClicked(
                                                                    unitType,
                                                                    descriptor
                                                                );
                                                            }}
                                                            onUnitTypeSelected={() => {
                                                                return;
                                                            }}
                                                        />
                                                    );
                                                }
                                            }
                                        )}
                                    </Carousel>
                                </div>
                            </>
                        )}
                        {descriptorList.length > 1 && (
                            <div className={`${classes.slider}`}>
                                <span>Smaller</span>
                                <input
                                    type='range'
                                    onChange={(e) => {
                                        setActiveDescriptor(parseInt(e.target.value));
                                    }}
                                    value={activeDescriptor}
                                    min={0}
                                    max={descriptorList.length - 1}
                                    step='1'
                                />
                                <span>Larger</span>
                            </div>
                        )}
                    </>
                )}
            </div>
            {quoteModalOpen && (
                <QuoteForm closeQuoteModal={() => setQuoteModalOpen(false)} />
            )}
        </>
    );
}

export default UnitListing;
